import { Button, Form } from 'antd';
import { FC, memo, ReactElement, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { CommonContainerTitle, FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
  PagesCriticalCssSettingsFormFields,
  PagesCriticalCssSettingsInputs,
  PagesQueryKeys,
  useGeneratePagesCriticalCssMutation,
  useGetPagesCriticalCssGenerationSummaryQuery,
  useGetPagesCriticalCssSettingsQuery,
  useSavePagesCriticalCssSettingsMutation,
} from '@/modules/Pages';

import { PagesCriticalCssSettingsOverviewView } from './PagesCriticalCssSettingsOverviewView';
import { pagesCriticalCssSettingsValidationSchema } from './validation';

interface Props {
  siteId: string;
}

export const PagesCriticalCssSettingsOverviewContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<PagesCriticalCssSettingsFormFields>();
  const appMessageApi = AppMessage.useControls();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PagesCriticalCssSettingsInputs>>(null);

  const {
    data: pagesCriticalCssSettingsData,
    error: pagesCriticalCssSettingsError,
    isLoading: isPagesCriticalCssSettingsLoading,
    isError: isPagesCriticalCssSettingsError,
  } = useGetPagesCriticalCssSettingsQuery(siteId)({
    enabled: !!siteId,
    onSuccess: (pagesCriticalCssSettingsData) => {
      form.setFieldsValue({
        screenDimensions: pagesCriticalCssSettingsData.screenDimensions,
        styleFiles: pagesCriticalCssSettingsData.styleFiles.map((file) => file.id),
      });
    },
  });

  const {
    data: generationSummary,
    isFetching: isGenerationSummaryFetching,
    refetch: refetchGenerationSummary,
  } = useGetPagesCriticalCssGenerationSummaryQuery(siteId)({
    enabled: !!siteId,
  });

  const { mutate: savePagesCriticalCssSettings, isLoading: isSaving } = useSavePagesCriticalCssSettingsMutation(siteId)({
    onSuccess: (data) => {
      queryClient.setQueryData([PagesQueryKeys.PagesCriticalCssSettings, siteId], data);
      appMessageApi.success('Налаштування Critical CSS успішно збережено!');
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const { mutateAsync: generatePagesCriticalCss, isLoading: isGenerating } = useGeneratePagesCriticalCssMutation(siteId)({
    onSuccess: async () => {
      appMessageApi.success('Запит на генерацію Critical CSS успішно відправлено!');
      await refetchGenerationSummary();
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.SitesById.replace(':siteId', siteId));
  }, [siteId]);

  const handleFinish = useCallback((values: PagesCriticalCssSettingsFormFields) => {
    setFormErrors(null);
    savePagesCriticalCssSettings(values);
  }, []);

  const handleFinishFailed = useCallback(() => {
    setFormErrors(null);
  }, []);

  const handleGenerate = async () => {
    await generatePagesCriticalCss()
  };

  const handleRefreshGenerationSummary = async () => {
    await refetchGenerationSummary();
  };

  return (
    <>
      <CommonContainerTitle
        onBack={handleBack}
        extra={
          <Button
            type="default"
            loading={isSaving || isGenerating || isPagesCriticalCssSettingsLoading}
            onClick={handleGenerate}
            disabled={isSaving || isGenerating || isPagesCriticalCssSettingsLoading}
          >
            Згенерувати (для всіх)
          </Button>
        }
      >
        Налаштування Critical CSS
      </CommonContainerTitle>

      <FallbackedLayout
        data={pagesCriticalCssSettingsData}
        isLoading={isPagesCriticalCssSettingsLoading}
        isError={isPagesCriticalCssSettingsError}
        error={pagesCriticalCssSettingsError}
      >
        {(pagesCriticalCssSettings): ReactElement => (
          <PagesCriticalCssSettingsOverviewView
            siteId={siteId}
            formName="pages-critical-css-settings-form"
            formType="edit"
            formErrors={formErrors}
            form={form}
            generationSummary={generationSummary}
            isGenerationSummaryLoading={isGenerationSummaryFetching}
            handleRefreshGenerationSummary={handleRefreshGenerationSummary}
            initialValues={{
              styleFiles: pagesCriticalCssSettings.styleFiles.map((file) => file.id),
              screenDimensions: pagesCriticalCssSettings.screenDimensions,
            }}
            validationSchema={pagesCriticalCssSettingsValidationSchema}
            handleBack={handleBack}
            handleFinish={handleFinish}
            handleFinishFailed={handleFinishFailed}
            isFormLoading={isSaving || isGenerating}
          />
        )}
      </FallbackedLayout>
    </>
  );
});
